/**
 * React Query hooks for fetching CDU configurations
 * @see GitLab #996 — Repeating Sections (Phase 2: Cooling)
 */

import { useQuery } from '@tanstack/react-query';

import { isRepeatingCollectionRouteNotFound } from './repeatingCollectionRouteNotFound';
import { useNetworkStatus } from './useOfflineRequestsQuery';
import { groupLiquidCoolingCdusByStatus } from '../../components/liquidcoolingcdus/liquidCoolingCduListUtils';
import { api } from '../../services/api';
import {
  applyPendingLiquidCoolingCduOperations,
  getCachedLiquidCoolingCduList,
  getOfflineLiquidCoolingCduById,
  getOfflineLiquidCoolingCduGrouped,
  getOfflineLiquidCoolingCduList,
  getInFlightLiquidCoolingCduOpsForRequest,
  mirrorLiquidCoolingCduToOfflineRequestCache,
} from '../../services/liquidCoolingCduOfflineSync';

import type {
  LiquidCoolingCduConfiguration,
  LiquidCoolingCduConfigurationsResponse,
  GroupedLiquidCoolingCduConfigurations,
} from '../../types/liquidCoolingCdu';

/**
 * Query keys for consistent cache management
 */
export const liquidCoolingCduConfigurationsKeys = {
  all: ['liquidCoolingCduConfigurations'] as const,
  lists: () => [...liquidCoolingCduConfigurationsKeys.all, 'list'] as const,
  list: (requestId: string) =>
    [...liquidCoolingCduConfigurationsKeys.lists(), requestId] as const,
  grouped: (requestId: string) =>
    [...liquidCoolingCduConfigurationsKeys.all, 'grouped', requestId] as const,
  details: () => [...liquidCoolingCduConfigurationsKeys.all, 'detail'] as const,
  detail: (requestId: string, liquidCoolingCduId: number) =>
    [
      ...liquidCoolingCduConfigurationsKeys.details(),
      requestId,
      liquidCoolingCduId,
    ] as const,
};

/**
 * Hook for fetching all CDU configurations for a request
 * Includes offline fallback to return cached configurations
 */
export const useLiquidCoolingCduConfigurations = (
  requestId: string,
  enabled = true,
) => {
  const isOnline = useNetworkStatus();

  return useQuery({
    queryKey: liquidCoolingCduConfigurationsKeys.list(requestId),
    queryFn: async () => {
      if (!isOnline) {
        return getOfflineLiquidCoolingCduList(requestId);
      }

      try {
        const response = await api.get<LiquidCoolingCduConfigurationsResponse>(
          `/requests/${requestId}/liquid-cooling-cdus`,
        );
        await mirrorLiquidCoolingCduToOfflineRequestCache(
          requestId,
          response.liquidCoolingCdus,
        );
        const pendingOps = await getInFlightLiquidCoolingCduOpsForRequest(
          requestId,
        );
        return applyPendingLiquidCoolingCduOperations(
          requestId,
          response.liquidCoolingCdus,
          pendingOps,
        );
      } catch (error) {
        if (isRepeatingCollectionRouteNotFound(error)) {
          return [];
        }
        throw error;
      }
    },
    enabled: !!requestId && enabled,
    staleTime: 30 * 1000, // 30 seconds
    retry: (failureCount, error) =>
      isRepeatingCollectionRouteNotFound(error) ? false : failureCount < 3,
    meta: {
      errorMessage: 'Failed to fetch CDU configurations.',
    },
  });
};

/**
 * Hook for fetching CDU configurations grouped by status (existing vs new)
 * Includes offline fallback (derived from cached list)
 */
export const useLiquidCoolingCduConfigurationsGrouped = (
  requestId: string,
  enabled = true,
) => {
  const isOnline = useNetworkStatus();

  return useQuery({
    queryKey: liquidCoolingCduConfigurationsKeys.grouped(requestId),
    queryFn: async () => {
      if (!isOnline) {
        return getOfflineLiquidCoolingCduGrouped(requestId);
      }

      const response = await api.get<GroupedLiquidCoolingCduConfigurations>(
        `/requests/${requestId}/liquid-cooling-cdus/grouped`,
      );
      const allUnits = [...response.existing, ...response.new];
      await mirrorLiquidCoolingCduToOfflineRequestCache(requestId, allUnits);
      const pendingOps = await getInFlightLiquidCoolingCduOpsForRequest(
        requestId,
      );
      return groupLiquidCoolingCdusByStatus(
        applyPendingLiquidCoolingCduOperations(requestId, allUnits, pendingOps),
      );
    },
    enabled: !!requestId && enabled,
    staleTime: 30 * 1000,
    meta: {
      errorMessage: 'Failed to fetch grouped CDU configurations.',
    },
  });
};

/**
 * Hook for fetching a single CDU configuration
 * Includes offline fallback from cached request units
 */
export const useLiquidCoolingCduConfiguration = (
  requestId: string,
  liquidCoolingCduId: number,
  enabled = true,
) => {
  const isOnline = useNetworkStatus();

  return useQuery({
    queryKey: liquidCoolingCduConfigurationsKeys.detail(
      requestId,
      liquidCoolingCduId,
    ),
    queryFn: async () => {
      if (!isOnline) {
        const cached = await getOfflineLiquidCoolingCduById(
          requestId,
          liquidCoolingCduId,
        );
        if (!cached) {
          throw new Error('CDU configuration not found in offline cache');
        }
        return cached;
      }

      const response = await api.get<LiquidCoolingCduConfiguration>(
        `/requests/${requestId}/liquid-cooling-cdus/${liquidCoolingCduId}`,
      );
      const list = await getCachedLiquidCoolingCduList(requestId);
      const merged = list.some(u => u.id === response.id)
        ? list.map(u => (u.id === response.id ? response : u))
        : [...list, response];
      await mirrorLiquidCoolingCduToOfflineRequestCache(requestId, merged);
      return response;
    },
    enabled: !!requestId && !!liquidCoolingCduId && enabled,
    staleTime: 30 * 1000,
    meta: {
      errorMessage: 'Failed to fetch CDU configuration details.',
    },
  });
};
